import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Crown, Check, Sparkles, Zap, Shield } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function AuthPage() {
  const { t } = useTranslation();
  const { login, signup, user, ownerQuickEntry } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ email: '', username: '', password: '', display_name: '' });
  const [ownerEntryCount, setOwnerEntryCount] = useState(0);
  const [rulesAccepted, setRulesAccepted] = useState(false);

  useEffect(() => {
    if (user) navigate('/feed', { replace: true });
  }, [user, navigate]);

  // Triple-click on logo area triggers owner quick entry
  const handleOwnerEntry = async () => {
    setOwnerEntryCount(prev => prev + 1);
    if (ownerEntryCount >= 2) {
      setLoading(true);
      try {
        await ownerQuickEntry();
        toast.success('Welcome back, Susan David Community');
        navigate('/feed');
      } catch {
        toast.error('Owner entry unavailable');
      } finally {
        setLoading(false);
        setOwnerEntryCount(0);
      }
    }
    // Reset count after 2 seconds
    setTimeout(() => setOwnerEntryCount(0), 2000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back');
      navigate('/feed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(signupData.email, signupData.username, signupData.password, signupData.display_name);
      toast.success('Welcome to PushPro! Your 30-day free trial starts now.');
      navigate('/feed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Left - Decorative */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-end p-12"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1761437856554-3072a8143bc3?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-obsidian/70" />
        <div className="relative z-10">
          <p className="text-gold text-xs uppercase tracking-[0.3em] mb-3">PushPro</p>
          <h1 className="font-serif text-4xl text-white leading-tight">Create, Promote &<br /><em className="text-gold">Sell Digital Products</em></h1>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16 relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-sm">
          {/* Logo - Triple click for owner quick entry */}
          <div 
            onClick={handleOwnerEntry}
            className="font-serif text-xl mb-10 block cursor-pointer select-none"
            title="Susan David Community"
          >
            <span className="text-obsidian">Push</span>
            <span className="text-gold ml-1">Pro</span>
            {ownerEntryCount > 0 && (
              <Crown size={12} className="inline ml-2 text-gold animate-pulse" />
            )}
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-sm bg-mist h-10 mb-8">
              <TabsTrigger value="login" data-testid="auth-tab-login" className="rounded-sm text-xs uppercase tracking-wider">{t('common.sign_in')}</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup" className="rounded-sm text-xs uppercase tracking-wider">{t('common.sign_up')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t('auth.email')}</Label>
                  <Input
                    data-testid="login-email"
                    type="email"
                    required
                    value={loginData.email}
                    onChange={e => setLoginData(p => ({ ...p, email: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t('auth.password')}</Label>
                  <Input
                    data-testid="login-password"
                    type="password"
                    required
                    value={loginData.password}
                    onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300"
                >
                  {loading ? t('auth.signing_in') : t('common.sign_in')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Display Name</Label>
                  <Input
                    data-testid="signup-displayname"
                    value={signupData.display_name}
                    onChange={e => setSignupData(p => ({ ...p, display_name: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Username</Label>
                  <Input
                    data-testid="signup-username"
                    required
                    value={signupData.username}
                    onChange={e => setSignupData(p => ({ ...p, username: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input
                    data-testid="signup-email"
                    type="email"
                    required
                    value={signupData.email}
                    onChange={e => setSignupData(p => ({ ...p, email: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                  <Input
                    data-testid="signup-password"
                    type="password"
                    required
                    minLength={6}
                    value={signupData.password}
                    onChange={e => setSignupData(p => ({ ...p, password: e.target.value }))}
                    className="rounded-none border-0 border-b border-input bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-obsidian"
                  />
                </div>
                <Button
                  data-testid="signup-submit-btn"
                  type="submit"
                  disabled={loading || !rulesAccepted}
                  className="w-full rounded-sm h-12 uppercase tracking-wider text-xs bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300 disabled:opacity-40"
                >
                  {loading ? 'Creating Account...' : 'Start Free Trial'}
                </Button>

                {/* Rules agreement */}
                <label className="flex items-start gap-2.5 cursor-pointer mt-3" data-testid="rules-agreement">
                  <input
                    type="checkbox"
                    checked={rulesAccepted}
                    onChange={e => setRulesAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-stone-300 accent-amber-600"
                  />
                  <span className="text-[10px] text-muted-foreground leading-relaxed">
                    I understand that AI tools require a paid subscription (Creator AI €49/mo or Studio €100/mo). 
                    The free trial includes community & marketplace access only. I agree to the{' '}
                    <Link to="/rules" className="text-gold hover:underline">Community Rules</Link>.
                  </span>
                </label>

                {/* 3-tier plan overview */}
                <div className="border border-amber-200/50 rounded-lg p-4 bg-amber-50/30 mt-4" data-testid="signup-membership-info">
                  <div className="flex items-center gap-2 mb-3">
                    <Crown size={14} className="text-amber-600" />
                    <span className="text-xs font-semibold text-obsidian">{t('auth.membership_tiers')}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-[11px]">
                      <Crown size={11} className="text-amber-500 shrink-0" />
                      <span className="text-obsidian/70"><strong className="text-obsidian">Marketplace €29/mo</strong> — Browse, sell, community</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <Sparkles size={11} className="text-violet-500 shrink-0" />
                      <span className="text-obsidian/70"><strong className="text-obsidian">Creator AI €49/mo</strong> — AI ebooks, planners, images</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <Zap size={11} className="text-rose-500 shrink-0" />
                      <span className="text-obsidian/70"><strong className="text-obsidian">Studio €100/mo</strong> — Full AI suite + video</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-600 mt-2.5 font-medium">30 days free to explore community & marketplace</p>
                </div>

                <p className="text-[10px] text-center text-muted-foreground mt-3">
                  <Link to="/pricing" className="text-gold hover:underline">Compare all plans</Link>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
